import React, {useCallback, useContext, useEffect, useState, useMemo} from "react";
import HierarchiesService from "../../../services/HierarchiesService";
import CacheService from "../../../services/CacheService";
import {Button, Form, Input, message, Modal, Popconfirm, Popover, Row, Space, Table, Switch} from 'antd';
import {useParams} from "react-router";
import {AuthContext} from "../../../contexts/AuthContext";

export function HierarchiesList({onView}) {
  const [hierarchiesList, setHierarchiesList] = useState([]);
  const [modalShown, setModalShown] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const auth = useContext(AuthContext)

  const params = useParams();

  const loadHierarchies = useCallback(() => {
    CacheService.getProjectData(params.projectId).then(projectData => {
      return HierarchiesService.fetchHierarchies(projectData.workspace_id).then(res => {
        setHierarchiesList(res.data);
      })
    }).catch(() => message.error('Failed to load hierarchies list'));
  }, [params]);

  const handleFilterChange = useCallback((newValue) => {
    setShowAll(newValue);
  }, []);
  
  const filteredList = useMemo(() => {
    return showAll ? hierarchiesList : hierarchiesList.filter(item => !item.hidden);
  }, [showAll, hierarchiesList]);

  const handleShowModal = useCallback(() => {
    setModalShown(true);
  }, []);

  const handleHideModal = useCallback(() => {
    setModalShown(false);
  }, []);

  const handleCreateNewHierarchy = useCallback(values => {
    CacheService.getProjectData(params.projectId).then(projectData => {
      const newHierarchy = {
        name: values.name,
        workspace_id: projectData.workspace_id,
      };
      HierarchiesService.addHierarchy(newHierarchy).then(res => {
        message.success('Created new hierarchy');
        handleHideModal();
        onView(res.data.id);
      }).catch(() => {
        message.error('Failed to create the new hierarchy');
      });
    });
  }, [handleHideModal, params, onView]);

  const handleDeleteHierarchy = useCallback(hierarchyId => {
    HierarchiesService.deleteHierarchy(hierarchyId).then(() => {
      message.success('Hierarchy removed');
      loadHierarchies();
    }).catch(() => {
      message.error('Failed to remove the hierarchy');
    })
  }, [loadHierarchies]);

  const handleRenameHierarchy = useCallback((hierarchyId, newName) => {
    HierarchiesService.renameHierarchy(hierarchyId, newName).then(() => {
      message.success('Hierarchy renamed');
      loadHierarchies();
    }).catch(() => {
      message.error('Failed to rename the hierarchy');
    });
  }, [loadHierarchies]);

  useEffect(() => {
    loadHierarchies();
  }, [loadHierarchies]);

  const isAdmin = auth.user && auth.user.roles.includes('admin');
  const hierarchiesTableColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Actions',
      key: 'id',
      render: (_, record) => {
        const id = record.id;

        return (
          <React.Fragment>
            <Space>
              <a onClick={() => onView(id)}>Open</a>
              <RenameHierarchy
                  currentName={record.name}
                  hierarchyId={id}
                  onRename={newName => handleRenameHierarchy(id, newName)}
              >
                <a>Rename</a>
              </RenameHierarchy>
              {isAdmin &&
                <Popconfirm
                  title="Are you sure to delete the hierarchy？"
                  onConfirm={() => handleDeleteHierarchy(id)}>
                    <a>Delete</a>
                </Popconfirm>
              }
            </Space>
          </React.Fragment>
        )
      }
    }
  ];

  const formLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };

  return (
    <React.Fragment>
      <Row style={{ marginBottom: 16 }}>
        <Space>
          <Switch checked={showAll} onChange={handleFilterChange} />
          <span>Show All</span>
        </Space>
        <Button onClick={handleShowModal} type="primary" style={{ marginLeft: 'auto' }}>
          New hierarchy
        </Button>
      </Row>
      <Table
        id="hierarchies-list-table"
        dataSource={filteredList}
        columns={hierarchiesTableColumns}
        rowKey="id"
      />

      <Modal
          title="Create new hierarchy"
          visible={modalShown}
          onCancel={handleHideModal}
          footer={null}
          // width="40vw"
          bodyStyle={{height: 'auto'}}
      >
        <Form {...formLayout} name="create-new-hierarchy" onFinish={handleCreateNewHierarchy}>
          <Form.Item name={'name'} label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row>
            <Button type="primary" htmlType="submit" style={{marginLeft: 'auto'}}>
              Create
            </Button>
          </Row>
        </Form>
      </Modal>
    </React.Fragment>
  )
}


function RenameHierarchy({currentName, hierarchyId, children, onRename}) {
  const [visible, setVisible] = useState(false);
  const formId = "hierarchy-name-validation-form";

  const content = (
      <div style={{width: "288px"}}>
        <Row style={{marginBottom: '8px'}}>
          <span>Type the hierarchy name in the field below</span>
        </Row>
        <RenameHierarchyForm
            id={formId}
            initialValue={{name: currentName}}
            onFinish={values => {
              setVisible(false);
              onRename(values.name);
            }}
        />
        <Row
            style={{justifyContent: "space-between"}}
        >
          <Button size="small" onClick={_ => setVisible(false)} style={{width: 90}}>Cancel</Button>
          <Button
              form={formId}
              key="submit" htmlType="submit"
              type="primary" size="small" style={{width: 90}}
          >
            Apply
          </Button>
        </Row>
      </div>
  );

  return (
      <Popover
          key={hierarchyId}
          visible={visible}
          onVisibleChange={value => setVisible(value)}
          destroyTooltipOnHide={true}
          trigger={"click"}
          content={content}
      >
        {children}
      </Popover>
  );
}


function RenameHierarchyForm({id, initialValue, onFinish}) {
  const formLayout = {
    labelCol: {span: 6},
    wrapperCol: {span: 18},
  };

  const nameRules = [
    {
      validator: async (_, target) => {
        if (target.replace(' ', '') !== '') return Promise.resolve();

        return Promise.reject(new Error("Name can't be empty"));
      }
    }
  ]

  return (
      <Form id={id}
            hideRequiredMark={true}
            onFinish={(values) => onFinish(values)}
            initialValues={initialValue}
            {...formLayout}
      >
        <Form.Item label="name"
                   name="name"
                   rules={nameRules}>
          <Input id="hierarchy-name-input" size="small"/>
        </Form.Item>
      </Form>
  );
}
